import { ReactNode, useMemo } from "react"
import ReactQRCode from "react-qr-code"
import {
  Box,
  Button,
  CircularProgress,
  Flex,
  Image,
  Text,
} from "@chakra-ui/react"

import copyIcon from "#/assets/copy-white.svg"
import TooltipLabel from "#/shared/components/TooltipLabel"
import { BITCOIN_MIN_DEPOSIT } from "#/shared/constants"
import { useCopyValue } from "#/shared/hooks"

type DepositModalQRProps = {
  depositAmount: string
  walletAddress: string | null
  bitcoinAddress: string | null
  description?: ReactNode
}

export default function DepositModalQR({
  depositAmount,
  walletAddress,
  bitcoinAddress,
  description,
}: DepositModalQRProps) {
  const { label, copy } = useCopyValue(bitcoinAddress, "Copy address")

  const bitcoinQRValue = useMemo(
    () => `bitcoin:${bitcoinAddress}?amount=${depositAmount}`,
    [bitcoinAddress, depositAmount],
  )

  if (!walletAddress) return null

  if (!bitcoinAddress) {
    return (
      <Flex w="100%" justifyContent="center">
        <CircularProgress
          isIndeterminate
          color="primary.black.100"
          trackColor="primary.white.100"
          size="120px"
        />
      </Flex>
    )
  }

  return (
    <>
      <Box bg="primary.black.100" p={8} mb={4} rounded="8px" w="100%">
        <Flex w="100%" gap={4}>
          <Flex
            bg="primary.white.100"
            h="142px"
            minW="142px"
            justifyContent="center"
            alignItems="center"
            rounded="4px"
          >
            <ReactQRCode value={bitcoinQRValue} size={110} />
          </Flex>
          <Box w="100%">
            <Box
              w="100%"
              px={4}
              py={2}
              borderStyle="solid"
              borderColor="primary.white.100"
              borderWidth="1px"
              rounded="8px"
              height="min-content"
              mb={6}
            >
              <Flex w="100%" justifyContent="space-between" alignItems="center">
                <Text
                  color="primary.white.100"
                  variant="body"
                  fontWeight={500}
                  fontFamily="riforma.medium"
                >
                  {`${bitcoinAddress.slice(0, 5)}...${bitcoinAddress.slice(
                    -5,
                  )}`}
                </Text>
                <TooltipLabel value={label} variant="light">
                  <Button variant="unstyled" onClick={copy}>
                    <Image src={copyIcon} />
                  </Button>
                </TooltipLabel>
              </Flex>
            </Box>
            <Flex gap={3}>
              <CircularProgress
                size="16px"
                isIndeterminate
                trackColor="primary.black.100"
                color="primary.white.100"
              />
              <Text
                color="primary.white.100"
                variant="body"
                fontWeight={500}
                fontFamily="riforma.medium"
              >
                Waiting for deposit...
              </Text>
            </Flex>
          </Box>
        </Flex>
      </Box>
      <Text variant="label" color="primary.black.80">
        <Text
          as="span"
          color="primary.red.100"
          fontWeight={700}
          fontFamily="riforma.bold"
        >
          Send over {BITCOIN_MIN_DEPOSIT} BTC from your wallet to above address.{" "}
        </Text>
        {description}
      </Text>
    </>
  )
}
